import React, { useState, useEffect, useRef } from "react";
import {
	CraftModal,
	CraftModalHeader,
	CraftModalBody,
	CraftModalFooter,
	CraftButton,
	CraftSpinner,
} from "@craft-design-system/core-components-react";
import {
	Pencil,
	Trash2,
	Save,
	X,
	Plus,
	RefreshCw as Refresh,
} from "lucide-react";
import { apiClient } from "../../services/api";

function ProjectInfoModal({ open, onClose }) {
	const [projects, setProjects] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [success, setSuccess] = useState(null);
	const [editingId, setEditingId] = useState(null);
	const [editedData, setEditedData] = useState({});
	const [isAdding, setIsAdding] = useState(false);
	const [isAuthorized, setIsAuthorized] = useState(false);
	const [newProject, setNewProject] = useState({
		projektnummer: "",
		projekttitel: "",
		programm: "",
		project_responsible: "",
	});
	const addRowRef = useRef(null);

	// Fetch project data
	const fetchProjects = async () => {
		setLoading(true);
		setError(null);
		try {
			const response = await apiClient.get("/api/project-info");
			setProjects(response.data.data || []);
			setIsAuthorized(response.data.isAuthorized || false);
		} catch (err) {
			console.error("Error fetching projects:", err);
			setError("Failed to load project information");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (open) {
			fetchProjects();
		}
	}, [open]);

	// Scroll the "add new" row into view when it appears
	useEffect(() => {
		if (isAdding && addRowRef.current) {
			addRowRef.current.scrollIntoView({
				behavior: "smooth",
				block: "nearest",
			});
		}
	}, [isAdding]);

	// Handle edit mode
	const handleEdit = (project) => {
		if (!isAuthorized) {
			setError(
				"Access denied. You are not authorized to edit project information.",
			);
			return;
		}
		setEditingId(project.projektnummer);
		setEditedData({ ...project });
		setIsAdding(false);
	};

	// Handle cancel edit
	const handleCancelEdit = () => {
		setEditingId(null);
		setEditedData({});
	};

	// Handle save edit
	const handleSaveEdit = async () => {
		setLoading(true);
		setError(null);
		setSuccess(null);
		try {
			await apiClient.put(`/api/project-info/${editingId}`, {
				projektnummer: editedData.projektnummer,
				projekttitel: editedData.projekttitel,
				programm: editedData.programm,
				project_responsible: editedData.project_responsible,
			});
			setSuccess("Project updated successfully");
			setEditingId(null);
			setEditedData({});
			await fetchProjects();
		} catch (err) {
			console.error("Error updating project:", err);
			setError(err.response?.data?.detail || "Failed to update project");
		} finally {
			setLoading(false);
		}
	};

	// Handle delete
	const handleDelete = async (projektnummer) => {
		if (!isAuthorized) {
			setError(
				"Access denied. You are not authorized to delete project information.",
			);
			return;
		}

		if (
			!window.confirm(
				`Are you sure you want to delete project ${projektnummer}?`,
			)
		) {
			return;
		}

		setLoading(true);
		setError(null);
		setSuccess(null);
		try {
			await apiClient.delete(`/api/project-info/${projektnummer}`);
			setSuccess("Project deleted successfully");
			await fetchProjects();
		} catch (err) {
			console.error("Error deleting project:", err);
			setError(err.response?.data?.detail || "Failed to delete project");
		} finally {
			setLoading(false);
		}
	};

	// Handle add new project
	const handleAddNew = () => {
		if (!isAuthorized) {
			setError(
				"Access denied. You are not authorized to create project information.",
			);
			return;
		}
		setIsAdding(true);
		setEditingId(null);
		setNewProject({
			projektnummer: "",
			projekttitel: "",
			programm: "",
			project_responsible: "",
		});
	};

	// Handle cancel add
	const handleCancelAdd = () => {
		setIsAdding(false);
		setNewProject({
			projektnummer: "",
			projekttitel: "",
			programm: "",
			project_responsible: "",
		});
	};

	// Handle save new project
	const handleSaveNew = async () => {
		if (!newProject.projektnummer.trim()) {
			setError("Project number is required");
			return;
		}

		setLoading(true);
		setError(null);
		setSuccess(null);
		try {
			await apiClient.post("/api/project-info", newProject);
			setSuccess("Project created successfully");
			setIsAdding(false);
			setNewProject({
				projektnummer: "",
				projekttitel: "",
				programm: "",
				project_responsible: "",
			});
			await fetchProjects();
		} catch (err) {
			console.error("Error creating project:", err);
			setError(err.response?.data?.detail || "Failed to create project");
		} finally {
			setLoading(false);
		}
	};

	// Handle input change for editing
	const handleEditChange = (field, value) => {
		setEditedData((prev) => ({
			...prev,
			[field]: value,
		}));
	};

	// Handle input change for new project
	const handleNewChange = (field, value) => {
		setNewProject((prev) => ({
			...prev,
			[field]: value,
		}));
	};

	const s = {
		// Constrained shell: ~40% smaller than the previous modal footprint.
		// The body scrolls internally so the footer Close button stays visible.
		shell: {
			width: "900px",
			maxWidth: "100%",
			display: "flex",
			flexDirection: "column",
			maxHeight: "68vh",
		},
		toolbar: {
			display: "flex",
			justifyContent: "space-between",
			alignItems: "center",
			marginBottom: "12px",
			flexShrink: 0,
		},
		count: {
			fontSize: "13px",
			color: "#666",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		toolbarBtns: {
			display: "flex",
			gap: "8px",
			alignItems: "center",
		},
		ghostBtn: {
			background: "none",
			border: "1px solid #ddd",
			borderRadius: "4px",
			padding: "5px 12px",
			cursor: "pointer",
			display: "flex",
			alignItems: "center",
			gap: "5px",
			fontSize: "12px",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
			color: "#555",
		},
		addBtn: {
			background: "none",
			border: "1px solid #4caf50",
			borderRadius: "4px",
			padding: "5px 12px",
			cursor: "pointer",
			display: "flex",
			alignItems: "center",
			gap: "5px",
			fontSize: "12px",
			fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
			color: "#2e7d32",
		},
		alert: {
			padding: "10px 14px",
			borderRadius: "6px",
			marginBottom: "12px",
			fontSize: "13px",
			backgroundColor: "#fde8e8",
			border: "1px solid #f5c6c6",
			color: "#cc0000",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		successAlert: {
			padding: "10px 14px",
			borderRadius: "6px",
			marginBottom: "12px",
			fontSize: "13px",
			backgroundColor: "#e8f5e9",
			border: "1px solid #b7dfb9",
			color: "#2e7d32",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		tableWrap: {
			border: "1px solid #e5e7eb",
			borderRadius: "8px",
			overflow: "auto",
			flex: 1,
			minHeight: 0,
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		table: {
			width: "100%",
			borderCollapse: "collapse",
			fontSize: "13px",
		},
		th: {
			textAlign: "left",
			padding: "10px 12px",
			backgroundColor: "#f8f9fa",
			borderBottom: "1px solid #e5e7eb",
			fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
			fontSize: "12px",
			color: "#374151",
			position: "sticky",
			top: 0,
			zIndex: 1,
		},
		td: {
			padding: "8px 12px",
			borderBottom: "1px solid #f0f0f0",
			color: "#333",
			verticalAlign: "middle",
		},
		input: {
			width: "100%",
			boxSizing: "border-box",
			padding: "6px 8px",
			border: "1px solid #ccc",
			borderRadius: "4px",
			fontSize: "13px",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		actionCell: {
			display: "flex",
			gap: "6px",
			alignItems: "center",
		},
		iconBtn: {
			background: "none",
			border: "none",
			cursor: "pointer",
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			padding: "4px",
			borderRadius: "4px",
		},
		empty: {
			textAlign: "center",
			padding: "48px 20px",
			color: "#999",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		loadingWrap: {
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			padding: "40px",
			gap: "12px",
		},
	};

	const colSpan = isAuthorized ? 5 : 4;

	return (
		<CraftModal show={open} allowClickOut={false} onCraftClose={onClose}>
			<CraftModalHeader
				header="Project Information"
				content="Manage project numbers, titles, programs and part trackers."
			/>
			<CraftModalBody>
				<div style={s.shell}>
					{/* Toolbar */}
					<div style={s.toolbar}>
						<span style={s.count}>
							{!loading &&
								`${projects.length} project${projects.length !== 1 ? "s" : ""}`}
						</span>
						<div style={s.toolbarBtns}>
							{isAuthorized && !isAdding && (
								<button
									type="button"
									style={s.addBtn}
									onClick={handleAddNew}
									disabled={loading}
								>
									<Plus size={13} /> Add Project
								</button>
							)}
							<button
								type="button"
								onClick={fetchProjects}
								disabled={loading}
								style={s.ghostBtn}
							>
								<Refresh size={13} /> Refresh
							</button>
						</div>
					</div>

					{/* Error / success messages */}
					{error && <div style={s.alert}>⚠️ {error}</div>}
					{success && <div style={s.successAlert}>✓ {success}</div>}

					{/* Loading */}
					{loading && (
						<div style={s.loadingWrap}>
							<CraftSpinner size="m" />
							<span
								style={{
									fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
									color: "#666",
								}}
							>
								Loading project information…
							</span>
						</div>
					)}

					{/* Table */}
					{!loading && (
						<div style={s.tableWrap}>
							<table style={s.table}>
								<thead>
									<tr>
										<th style={s.th}>Project Number</th>
										<th style={s.th}>Project Title</th>
										<th style={s.th}>Program</th>
										<th style={s.th}>Part Tracker</th>
										{isAuthorized && (
											<th style={{ ...s.th, width: "110px" }}>Actions</th>
										)}
									</tr>
								</thead>
								<tbody>
									{/* Existing projects */}
									{projects.map((project) => {
										const isEditing = editingId === project.projektnummer;
										return (
											<tr
												key={project.projektnummer}
												style={{
													backgroundColor: isEditing ? "#fff8e1" : "white",
												}}
											>
												<td style={s.td}>
													{isEditing ? (
														<input
															style={s.input}
															value={editedData.projektnummer || ""}
															onChange={(e) =>
																handleEditChange(
																	"projektnummer",
																	e.target.value,
																)
															}
														/>
													) : (
														<span
															style={{
																fontFamily:
																	"MAN EUROPE_Bold, Arial, sans-serif",
															}}
														>
															{project.projektnummer}
														</span>
													)}
												</td>
												<td style={s.td}>
													{isEditing ? (
														<input
															style={s.input}
															value={editedData.projekttitel || ""}
															onChange={(e) =>
																handleEditChange("projekttitel", e.target.value)
															}
														/>
													) : (
														project.projekttitel
													)}
												</td>
												<td style={s.td}>
													{isEditing ? (
														<input
															style={s.input}
															value={editedData.programm || ""}
															onChange={(e) =>
																handleEditChange("programm", e.target.value)
															}
														/>
													) : (
														project.programm
													)}
												</td>
												<td style={s.td}>
													{isEditing ? (
														<input
															style={s.input}
															value={editedData.project_responsible || ""}
															onChange={(e) =>
																handleEditChange(
																	"project_responsible",
																	e.target.value,
																)
															}
														/>
													) : (
														project.project_responsible
													)}
												</td>
												{isAuthorized && (
													<td style={s.td}>
														<div style={s.actionCell}>
															{isEditing ? (
																<>
																	<button
																		type="button"
																		title="Save"
																		style={{ ...s.iconBtn, color: "#4caf50" }}
																		onClick={handleSaveEdit}
																	>
																		<Save size={16} />
																	</button>
																	<button
																		type="button"
																		title="Cancel"
																		style={{ ...s.iconBtn, color: "#cc0000" }}
																		onClick={handleCancelEdit}
																	>
																		<X size={16} />
																	</button>
																</>
															) : (
																<>
																	<button
																		type="button"
																		title="Edit"
																		style={{ ...s.iconBtn, color: "#666" }}
																		onClick={() => handleEdit(project)}
																	>
																		<Pencil size={16} />
																	</button>
																	<button
																		type="button"
																		title="Delete"
																		style={{ ...s.iconBtn, color: "#cc0000" }}
																		onClick={() =>
																			handleDelete(project.projektnummer)
																		}
																	>
																		<Trash2 size={16} />
																	</button>
																</>
															)}
														</div>
													</td>
												)}
											</tr>
										);
									})}

									{/* Add new row */}
									{isAdding && (
										<tr ref={addRowRef} style={{ backgroundColor: "#f0f8ff" }}>
											<td style={s.td}>
												<input
													style={s.input}
													value={newProject.projektnummer}
													onChange={(e) =>
														handleNewChange("projektnummer", e.target.value)
													}
													placeholder="Project No"
												/>
											</td>
											<td style={s.td}>
												<input
													style={s.input}
													value={newProject.projekttitel}
													onChange={(e) =>
														handleNewChange("projekttitel", e.target.value)
													}
													placeholder="Project Title"
												/>
											</td>
											<td style={s.td}>
												<input
													style={s.input}
													value={newProject.programm}
													onChange={(e) =>
														handleNewChange("programm", e.target.value)
													}
													placeholder="Program"
												/>
											</td>
											<td style={s.td}>
												<input
													style={s.input}
													value={newProject.project_responsible}
													onChange={(e) =>
														handleNewChange(
															"project_responsible",
															e.target.value,
														)
													}
													placeholder="Responsible"
												/>
											</td>
											{isAuthorized && (
												<td style={s.td}>
													<div style={s.actionCell}>
														<button
															type="button"
															title="Save"
															style={{ ...s.iconBtn, color: "#4caf50" }}
															onClick={handleSaveNew}
														>
															<Save size={16} />
														</button>
														<button
															type="button"
															title="Cancel"
															style={{ ...s.iconBtn, color: "#cc0000" }}
															onClick={handleCancelAdd}
														>
															<X size={16} />
														</button>
													</div>
												</td>
											)}
										</tr>
									)}

									{/* Empty state */}
									{projects.length === 0 && !isAdding && (
										<tr>
											<td colSpan={colSpan} style={s.empty}>
												{isAuthorized
													? "No project information available. Use “Add Project” to create one."
													: "No project information available."}
											</td>
										</tr>
									)}
								</tbody>
							</table>
						</div>
					)}
				</div>
			</CraftModalBody>
			<CraftModalFooter>
				<CraftButton variant="primary" size="m" onClick={onClose}>
					Close
				</CraftButton>
			</CraftModalFooter>
		</CraftModal>
	);
}

export default ProjectInfoModal;
